/**
 * <search-drawer>
 *
 * Search panel rendered once by snippets/jump-search-drawer.liquid. Every
 * magnifier in the header opens this single instance: the one in the header
 * icons, and .lupa-desk in the desktop dropdown menu.
 *
 * Opening does not touch <predictive-search> at all: the default state
 * (suggestions + best sellers) and the AJAX results are swapped by CSS off the
 * `open`/`loading` attributes that PredictiveSearch already manages.
 */
const SEARCH_DRAWER_TRIGGERS = '[data-search-drawer-open], .lupa-desk';

class SearchDrawer extends HTMLElement {
  constructor() {
    super();

    this.panel = this.querySelector('.search-drawer__panel');
    this.overlay = this.querySelector('.search-drawer__overlay');
    this.input = this.querySelector('input[type="search"]');
    this.activeTrigger = null;

    this.desktop = window.matchMedia('(min-width: 1024px)');
    this.onReposition = () => this.isOpen() && this.position();

    /**
     * The desktop panel is fixed to coordinates measured when it opened, so
     * scrolling would leave it stranded mid-page while the header moves away.
     * Re-anchor on every scroll, and give up once the header has left the
     * viewport — this theme's sticky header hides on scroll down, and a dropdown
     * pointing at nothing should close. Mobile locks the page scroll instead.
     */
    this.onScroll = () => {
      if (!this.isOpen() || this.scrollFrame) return;

      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null;
        if (!this.isOpen() || !this.desktop.matches) return;

        const rect = this.anchorRect();
        if (!rect || rect.bottom <= 0) {
          this.close();
          return;
        }

        this.position();
      });
    };

    // The overlay only reliably catches outside clicks when it wins the stacking
    // contest, which third-party widgets can break. This is the dependable path:
    // anything pressed outside the panel closes the drawer.
    this.onOutsidePointer = (event) => {
      if (this.panel?.contains(event.target)) return;
      if (event.target.closest?.(SEARCH_DRAWER_TRIGGERS)) return;
      this.close();
    };

    this.overlay?.addEventListener('click', () => this.close());
    this.querySelector('[data-search-drawer-close]')?.addEventListener('click', () => this.close());
    this.addEventListener('keyup', (event) => {
      if (event.code === 'Escape') this.close();
    });
  }

  isOpen() {
    return this.hasAttribute('open');
  }

  anchorRect() {
    // The .header__search wrapper is this element's own parent and still holds
    // the exact slot the header's search field used to occupy.
    const wrapper = this.closest('.header__search') || this.parentElement;
    return wrapper?.getBoundingClientRect() || null;
  }

  /**
   * Desktop drops the panel into the header's search slot: the pill lands where
   * the field used to sit and the results hang below it. The wrapper is empty on
   * desktop, so it collapses to a zero-height flex item centred in its row —
   * hence centring the pill on it rather than aligning to its top.
   * Mobile ignores all of this and covers the screen.
   */
  position() {
    const rect = this.anchorRect();
    if (!rect) return;

    const field = this.querySelector('.search-drawer__field');
    const fieldHeight = field?.offsetHeight || 52;
    const top = rect.top + rect.height / 2 - fieldHeight / 2;

    this.style.setProperty('--search-drawer-top', `${Math.max(top, 0)}px`);
    this.style.setProperty('--search-drawer-left', `${rect.left}px`);
    this.style.setProperty('--search-drawer-width', `${rect.width}px`);

    // Second pass: the CSS caps the panel at the same 74.2rem the header's field
    // used, so centre whatever width survived that cap inside the slot.
    const actual = this.panel?.getBoundingClientRect().width || 0;
    if (actual && actual < rect.width) {
      this.style.setProperty('--search-drawer-left', `${rect.left + (rect.width - actual) / 2}px`);
    }
  }

  open(trigger) {
    if (this.isOpen()) return;

    this.activeTrigger = trigger || null;
    this.position();

    this.setAttribute('open', '');
    document.body.classList.add('search-drawer-open');
    // A dropdown shouldn't freeze the page; a full-screen mobile panel should.
    if (!this.desktop.matches) document.body.classList.add('overflow-hidden');

    window.addEventListener('resize', this.onReposition);
    window.addEventListener('scroll', this.onScroll, { passive: true });
    document.addEventListener('pointerdown', this.onOutsidePointer);
    trapFocus(this.panel, this.input);
  }

  close() {
    if (!this.isOpen()) return;

    this.removeAttribute('open');
    document.body.classList.remove('overflow-hidden', 'search-drawer-open');
    window.removeEventListener('resize', this.onReposition);
    window.removeEventListener('scroll', this.onScroll);
    document.removeEventListener('pointerdown', this.onOutsidePointer);

    if (this.scrollFrame) {
      cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = null;
    }

    removeTrapFocus(this.activeTrigger);
    this.activeTrigger = null;
  }
}

customElements.define('search-drawer', SearchDrawer);

document.addEventListener('click', (event) => {
  const trigger = event.target.closest(SEARCH_DRAWER_TRIGGERS);
  if (!trigger) return;

  const drawer = trigger.closest('search-drawer') || document.querySelector('search-drawer');
  if (!drawer) return;

  // In A/B mode both searches are in the DOM and one is display: none. .lupa-desk
  // sits outside the drawer's subtree, so without this a control visitor would
  // drive a drawer they cannot see.
  const variant = drawer.closest('[data-search-variant]');
  if (variant && getComputedStyle(variant).display === 'none') return;

  event.preventDefault();
  drawer.isOpen() ? drawer.close() : drawer.open(trigger);
  if (drawer.isOpen()) drawer.input?.focus();
});
